import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import SEO from "@/components/SEO";
import { LayoutGrid, Users, Inbox, CreditCard, Repeat, Mail, FileText, LogOut, ArrowRight, BookMarked, ShieldCheck, ClipboardList, Search } from "lucide-react";
import VerificationHealthChip from "@/components/VerificationHealthChip";

const NAV = [
  { to: "/admin",              label: "Overview",     icon: LayoutGrid, end: true },
  { to: "/admin/clients",      label: "Clients",      icon: Users },
  { to: "/admin/leads",        label: "Leads",        icon: Inbox },
  { to: "/admin/plans",        label: "Plans",        icon: ClipboardList },
  { to: "/admin/mishap-analyses", label: "Mishaps",   icon: ClipboardList },
  { to: "/admin/entitlements",   label: "Entitlements", icon: ClipboardList },
  { to: "/admin/media",          label: "Media",        icon: ClipboardList },
  { to: "/admin/seo",            label: "SEO",          icon: Search },
  { to: "/admin/payments",     label: "Payments",     icon: CreditCard },
  { to: "/admin/referrals",    label: "Referrals",    icon: Repeat },
  { to: "/admin/emails",       label: "Emails",       icon: Mail },
  { to: "/admin/blog",         label: "Blog",         icon: FileText },
  { to: "/admin/catalog/institutions", label: "Catalog", icon: BookMarked },
  { to: "/admin/verification", label: "Verification", icon: ShieldCheck },
];

export default function AdminShell() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect unauthed users to /login via effect (avoids "setState during render" warning).
  useEffect(() => {
    if (!authLoading && !user) {
      navigate(`/login?return=${encodeURIComponent(location.pathname)}`, { replace: true });
    }
  }, [authLoading, user, location.pathname, navigate]);

  if (authLoading) return <div className="min-h-screen bg-bone flex items-center justify-center text-ink-soft">Loading…</div>;
  if (!user) return <div className="min-h-screen bg-bone flex items-center justify-center text-ink-soft">Redirecting to sign-in…</div>;
  if (user.role !== "admin") return <Restricted />;
  const initials = (user.name || user.email || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-bone flex" data-testid="admin-shell">
      <SEO noindex title="Admin — Beyond Valencia" canonical="/admin" />
      <aside className="hidden lg:flex flex-col w-60 border-r border-stone/40 bg-bone-dim min-h-screen sticky top-0">
        <Link to="/" className="px-5 pt-6 pb-4 border-b border-stone/30">
          <div className="font-serif text-lg text-ink tracking-tight leading-tight">Beyond<br />Valencia</div>
          <div className="mt-2 inline-block border border-brass px-2 py-0.5 text-[9px] uppercase tracking-[0.22em] text-brass" data-testid="admin-badge">Operations · Admin</div>
        </Link>
        <nav className="flex-1 py-4 px-2 space-y-0.5">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={n.end}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2 text-[13px] transition-colors ${isActive ? "bg-ivy text-bone" : "text-ink-soft hover:bg-stone/10 hover:text-ink"}`}
                data-testid={`admin-nav-${n.label.toLowerCase()}`}>
                <Icon className="h-3.5 w-3.5" /><span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-stone/30 p-3">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 bg-brass text-bone flex items-center justify-center font-serif text-xs">{initials}</div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-ink truncate font-medium">{user.name || user.email}</div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-brass">Admin</div>
            </div>
          </div>
          <Link to="/dashboard" className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-stone hover:text-ivy" data-testid="admin-switch-portal">
            Switch to student portal <ArrowRight className="h-3 w-3" />
          </Link>
          <button onClick={async () => { await logout(); navigate("/", { replace: true }); }} className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-stone hover:text-ink" data-testid="admin-logout">
            <LogOut className="h-3 w-3" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="border-b border-stone/30 bg-bone px-5 sm:px-7 py-3 flex items-center gap-3 text-[10px] uppercase tracking-[0.22em]">
          <span className="text-stone">Operations</span>
          <span className="text-stone">/</span>
          <span className="text-ivy font-semibold">{location.pathname.split("/").slice(2).join(" / ") || "overview"}</span>
          <VerificationHealthChip role={user.role} />
        </div>
        <div className="px-5 sm:px-7 py-7 lg:py-8 max-w-[1400px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function Restricted() {
  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-5" data-testid="admin-restricted">
      <div className="max-w-md text-center">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Restricted</div>
        <h1 className="font-serif text-3xl text-ink mt-3 tracking-tight">This area is restricted.</h1>
        <p className="text-ink-soft mt-4 text-sm leading-relaxed">Operations Dashboard is for Beyond Valencia administrators only.</p>
        <Link to="/dashboard" className="mt-7 inline-block border border-ivy bg-ivy text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-6">Go to your dashboard</Link>
      </div>
    </div>
  );
}
